import React, { useEffect, useState } from "react";
import { Plus, Check, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { formatFCFA, formatDate } from "../lib/format";
import { PageHeader, Spinner, StatusBadge } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Subscriptions() {
  const { user } = useAuth();
  const isCentral = ["principal_admin", "finance_agent"].includes(user?.role);
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ customer_id: "", plan: "starter", year: new Date().getFullYear() });

  const load = () => api.get("/subscriptions").then((r) => setSubs(r.data)).catch(() => setSubs([]));
  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    load();
  }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/subscriptions", { ...form, year: Number(form.year) });
      toast.success("Abonnement cree");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!subs) return <Spinner />;

  return (
    <div>
      <PageHeader
        title="Abonnements & formules"
        subtitle="Formules annuelles Starter, Standard et Premium souscrites par les groupes scolaires."
        actions={isCentral && (
          <Button data-testid="add-subscription-btn" onClick={() => setOpen(true)} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="h-4 w-4 mr-1.5" /> Nouvel abonnement
          </Button>
        )}
      />

      <div className="grid gap-6 md:grid-cols-3 mb-8">
        {plans.map((p, i) => (
          <Card key={p.id} data-testid={`plan-${p.id}`} className={`p-6 relative ${i === 2 ? "ring-2 ring-teal-500" : ""}`}>
            {i === 2 && <span className="absolute -top-3 left-6 rounded-full bg-teal-600 px-3 py-0.5 text-xs font-semibold text-white">Recommande</span>}
            <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
            <p className="text-sm text-slate-500 mt-1 min-h-[40px]">{p.description}</p>
            <p className="mt-4 font-mono text-2xl font-bold text-slate-900">{formatFCFA(p.annual_amount)}<span className="text-sm font-normal text-slate-400"> /an</span></p>
            <ul className="mt-5 space-y-2">
              {p.features.map((f, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-teal-500 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-display font-semibold text-slate-900">Abonnements actifs</h3></div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="px-6 py-4">Client</TableHead>
              <TableHead>Formule</TableHead>
              <TableHead>Exercice</TableHead>
              <TableHead>Periode</TableHead>
              <TableHead className="text-right">Montant annuel</TableHead>
              <TableHead>Statut</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subs.map((s) => (
              <TableRow key={s.id} data-testid={`table-row-${s.id}`}>
                <TableCell className="px-6 py-4 font-semibold text-slate-900">{s.customer_name}</TableCell>
                <TableCell>{s.plan_name}</TableCell>
                <TableCell className="font-mono">{s.year}</TableCell>
                <TableCell className="text-sm text-slate-500">{formatDate(s.start_date)} — {formatDate(s.end_date)}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{formatFCFA(s.annual_amount)}</TableCell>
                <TableCell><StatusBadge status={s.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Nouvel abonnement</DialogTitle>
          <DialogDescription>Souscrivez une formule annuelle pour un groupe scolaire.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Client</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="sub-customer" className="mt-1.5"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                <SelectContent className="bg-white z-50">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Formule</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger data-testid="sub-plan" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatFCFA(p.annual_amount)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Exercice</Label>
                <Select value={String(form.year)} onValueChange={(v) => setForm({ ...form, year: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    {[2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="sub-save" onClick={create} disabled={saving || !form.customer_id} className="bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Creer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
